'use client';

import { type ReactNode, useMemo } from 'react';

import styled from '@emotion/styled';
import { cn } from 'cn';
import { useTranslations } from 'use-intl';

import { type Messages } from '@/shared-ui';
import { OptionRenderType, ProjectType } from '@/survey-graphql';

import { getTypeOfRenderer } from './project-response-chat.helpers';
import ProjectUsabilityHandsFreeQuestionPopupBody from './project-usability-handsfree-question-popup-body';
import { HandsFreeQuestionPopupContext } from './project-usability-handsfree-question-popup-context';
import ProjectUsabilityHandsFreeQuestionPopupFooter from './project-usability-handsfree-question-popup-footer';
import ProjectUsabilityHandsFreeQuestionPopupHeader from './project-usability-handsfree-question-popup-header';

type ChatMessage = Messages[number];

const PopupArrow = styled.div<{ rightPx: number }>`
  right: ${(props) => props.rightPx}px;
`;

interface Props {
  currentQuestionIndex: number;
  totalQuestions: number;
  message: ChatMessage;
  projectType?: ProjectType;
  className?: string;
  variant?: 'dialog' | 'sheet';
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: ReactNode;
}

function ProjectUsabilityHandsFreeQuestionPopup({
  currentQuestionIndex,
  totalQuestions,
  message,
  projectType,
  className,
  variant = 'dialog',
  onMouseEnter,
  onMouseLeave,
  children,
}: Props) {
  const t = useTranslations('UserTest');

  const questionNumber = currentQuestionIndex + 1;
  const arrowRightPx = (totalQuestions - 1 - currentQuestionIndex) * 8 + 4;

  const messageType = getTypeOfRenderer(
    {
      ...message,
      createdAt: new Date(message.createdAt),
      optionRenderType: message.optionRenderType ?? OptionRenderType.Text,
    },
    projectType
  );

  const contextValue = useMemo(
    () => ({
      currentQuestionIndex,
      totalQuestions,
      message,
      messageType,
      projectType,
    }),
    [currentQuestionIndex, totalQuestions, message, messageType, projectType]
  );

  if (variant === 'sheet') {
    return (
      <HandsFreeQuestionPopupContext value={contextValue}>
        <div
          className={cn('flex w-full flex-col gap-2', className)}
          data-test='handsfree-task-panel'
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {children}
        </div>
      </HandsFreeQuestionPopupContext>
    );
  }

  return (
    <HandsFreeQuestionPopupContext value={contextValue}>
      <dialog
        open
        className={cn(
          'absolute inset-auto right-0 bottom-full m-0 mb-3 w-100 max-w-[calc(100vw-2rem)] animate-slide-in-from-bottom border-0 bg-transparent p-0 sm:max-w-none',
          className
        )}
        data-test='handsfree-task-panel'
        aria-label={t('task.counter', {
          current: questionNumber,
          total: totalQuestions,
        })}
      >
        <div
          className='relative flex flex-col gap-2 rounded-2xl bg-white px-6 py-4 shadow-[0px_15px_25px_0px_rgba(11,12,13,0.24),0px_5px_10px_0px_rgba(11,12,13,0.16)]'
          data-test='handsfree-question-popup'
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {children}
        </div>

        <PopupArrow
          rightPx={arrowRightPx}
          className='absolute -bottom-2 size-0 border-x-8 border-t-8 border-x-transparent border-t-white'
          aria-hidden='true'
        />
      </dialog>
    </HandsFreeQuestionPopupContext>
  );
}

ProjectUsabilityHandsFreeQuestionPopup.Header =
  ProjectUsabilityHandsFreeQuestionPopupHeader;
ProjectUsabilityHandsFreeQuestionPopup.Body =
  ProjectUsabilityHandsFreeQuestionPopupBody;
ProjectUsabilityHandsFreeQuestionPopup.Footer =
  ProjectUsabilityHandsFreeQuestionPopupFooter;

export default ProjectUsabilityHandsFreeQuestionPopup;
